import { useEffect, useState, useMemo, useCallback } from 'react';
import { useAuth } from '../lib/auth.js';
import { api } from '../lib/api.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { TableLoader } from '../components/TableLoader.js';
import { Pagination } from '../components/Pagination.js';
import { formatDateTime } from '../lib/dateUtils';
import type { TrashItem, TrashEntityType, TrashSummary } from '@gng-ehs/shared';

const TYPE_CONFIG: Record<TrashEntityType, { label: string; icon: string; bg: string; border: string; text: string }> = {
  form9: { label: 'Form 9', icon: '📋', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
  form10: { label: 'Form 10', icon: '📋', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
  form11: { label: 'Form 11', icon: '🧪', bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700' },
  training: { label: 'Training', icon: '🎓', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
  equipment: { label: 'Equipment', icon: '⚙️', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
  company: { label: 'Company', icon: '🏭', bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700' },
  personnel: { label: 'Personnel', icon: '👤', bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700' },
};

export function TrashPage() {
  const { canWrite } = useAuth();
  const hasWritePerm = canWrite('trash');

  const [items, setItems] = useState<TrashItem[]>([]);
  const [summary, setSummary] = useState<TrashSummary>({
    all: 0,
    form9: 0,
    form10: 0,
    form11: 0,
    training: 0,
    equipment: 0,
    company: 0,
    personnel: 0,
  });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filtering & Pagination
  const [selectedType, setSelectedType] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Multi-selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Confirm dialogs
  const [restoreItem, setRestoreItem] = useState<TrashItem | null>(null);
  const [deleteForeverItem, setDeleteForeverItem] = useState<TrashItem | null>(null);
  const [emptyTrashDialogOpen, setEmptyTrashDialogOpen] = useState(false);
  const [bulkRestoreDialogOpen, setBulkRestoreDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.trash.list({
        search: search.trim() || undefined,
        entity_type: selectedType !== 'all' ? selectedType : undefined,
        page,
        limit: 50,
        sortBy: 'deleted_at',
        sortOrder: 'desc',
      });
      const data = res.data as unknown as TrashItem[];
      setItems(data || []);
      setTotalPages(res.totalPages || 1);
      setTotal(res.total || 0);
      if ((res as unknown as { summary?: TrashSummary }).summary) {
        setSummary((res as unknown as { summary: TrashSummary }).summary);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load trash items');
    } finally {
      setLoading(false);
    }
  }, [search, selectedType, page]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Clear success message after 4s
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(''), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  // Selection helpers
  const toggleSelectAll = () => {
    if (selectedIds.size === items.length && items.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((i) => i.id)));
    }
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  // Restore single item
  const handleRestore = async (item: TrashItem) => {
    setActionLoading(true);
    setError('');
    try {
      await api.trash.restore(item.id);
      setSuccess(`"${item.title}" was restored successfully.`);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to restore item');
    } finally {
      setActionLoading(false);
      setRestoreItem(null);
    }
  };

  // Delete single item forever
  const handleDeleteForever = async (item: TrashItem) => {
    setActionLoading(true);
    setError('');
    try {
      await api.trash.deleteForever(item.id);
      setSuccess(`"${item.title}" was permanently deleted.`);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete item permanently');
    } finally {
      setActionLoading(false);
      setDeleteForeverItem(null);
    }
  };

  // Bulk restore
  const handleBulkRestore = async () => {
    if (!selectedIds.size) return;
    setActionLoading(true);
    setError('');
    try {
      const res = await api.trash.bulkRestore(Array.from(selectedIds));
      if (res.data?.errors && res.data.errors.length) {
        setError(`Restored ${res.data.count} items with some issues: ${res.data.errors.join(', ')}`);
      } else {
        setSuccess(`Successfully restored ${res.data?.count ?? selectedIds.size} items.`);
      }
      setSelectedIds(new Set());
      loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to restore selected items');
    } finally {
      setActionLoading(false);
      setBulkRestoreDialogOpen(false);
    }
  };

  // Bulk delete forever
  const handleBulkDelete = async () => {
    if (!selectedIds.size) return;
    setActionLoading(true);
    setError('');
    try {
      const res = await api.trash.bulkDelete(Array.from(selectedIds));
      setSuccess(`Permanently deleted ${res.data?.count ?? selectedIds.size} items.`);
      setSelectedIds(new Set());
      loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to permanently delete selected items');
    } finally {
      setActionLoading(false);
      setBulkDeleteDialogOpen(false);
    }
  };

  // Empty entire trash (or filtered type)
  const handleEmptyTrash = async () => {
    setActionLoading(true);
    setError('');
    try {
      const targetType = selectedType !== 'all' ? selectedType : undefined;
      const res = await api.trash.emptyTrash(targetType);
      setSuccess(res.data?.message || 'Trash emptied successfully.');
      setSelectedIds(new Set());
      loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to empty trash');
    } finally {
      setActionLoading(false);
      setEmptyTrashDialogOpen(false);
    }
  };

  const formatDeletedDate = (dateStr: string) => {
    return formatDateTime(dateStr);
  };

  const filterTabs = useMemo(() => [
    { key: 'all', label: 'All Items', icon: '🗑️', count: summary.all },
    { key: 'form9', label: 'Form 9', icon: '📋', count: summary.form9 },
    { key: 'form10', label: 'Form 10', icon: '📋', count: summary.form10 },
    { key: 'form11', label: 'Form 11', icon: '🧪', count: summary.form11 },
    { key: 'training', label: 'Training', icon: '🎓', count: summary.training },
    { key: 'equipment', label: 'Equipment', icon: '⚙️', count: summary.equipment },
    { key: 'company', label: 'Companies', icon: '🏭', count: summary.company },
    { key: 'personnel', label: 'Personnel', icon: '👤', count: summary.personnel },
  ], [summary]);

  return (
    <div className="flex-1 flex flex-col min-h-0 space-y-4 overflow-hidden pt-1 px-1">
      {/* ── Top Bar & 30-Day Notice ───────────────── */}
      <div className="shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">🗑️</span>
            <h2 className="text-lg font-bold text-gray-900">Trash / Recycle Bin</h2>
            <span className="ml-2 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
              {summary.all} items
            </span>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 mt-1 flex items-center gap-1.5">
            <span>⏱️</span>
            <span>Items remain in Trash for <strong>30 days</strong> before being permanently purged automatically.</span>
          </p>
        </div>

        {hasWritePerm && summary.all > 0 && (
          <button
            onClick={() => setEmptyTrashDialogOpen(true)}
            disabled={actionLoading}
            className="btn btn-secondary text-xs sm:text-sm text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 flex items-center gap-1.5 self-start sm:self-auto shrink-0"
          >
            <span>🔥</span>
            <span>Empty Trash{selectedType !== 'all' ? ` (${TYPE_CONFIG[selectedType as TrashEntityType]?.label || selectedType})` : ''}</span>
          </button>
        )}
      </div>

      {/* ── Error & Success Banners ──────────────── */}
      {error && (
        <div className="shrink-0 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span>⚠️</span>
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 text-sm font-semibold">✕</button>
        </div>
      )}
      {success && (
        <div className="shrink-0 p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg text-sm flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span>✅</span>
            <span>{success}</span>
          </div>
          <button onClick={() => setSuccess('')} className="text-emerald-500 hover:text-emerald-700 text-sm font-semibold">✕</button>
        </div>
      )}

      {/* ── Filter Tabs & Search Bar ─────────────── */}
      <div className="shrink-0 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        {/* Entity Type Filter Tabs */}
        <div className="flex items-center flex-wrap gap-1.5 no-scrollbar">
          {filterTabs.map((tab) => {
            const active = selectedType === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => { setSelectedType(tab.key); setPage(1); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  active
                    ? 'bg-brand-600 text-white shadow-xs font-semibold'
                    : 'bg-white text-gray-600 hover:bg-gray-100 hover:text-gray-900 border border-gray-200'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
                <span className={`ml-1 px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                  active ? 'bg-brand-700 text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search Input */}
        <div className="relative w-full lg:w-64 shrink-0">
          <input
            type="text"
            placeholder="Search trash items..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="input text-xs sm:text-sm pl-8 pr-8 w-full"
          />
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">🔍</span>
          {search && (
            <button
              onClick={() => { setSearch(''); setPage(1); }}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs cursor-pointer"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* ── Bulk Actions Bar ──────────────────────── */}
      {selectedIds.size > 0 && hasWritePerm && (
        <div className="shrink-0 flex items-center justify-between gap-3 bg-brand-50 border border-brand-200 px-4 py-2 rounded-lg text-xs sm:text-sm">
          <div className="flex items-center gap-2 text-brand-900 font-medium">
            <span>✨</span>
            <span><strong>{selectedIds.size}</strong> item{selectedIds.size > 1 ? 's' : ''} selected</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setBulkRestoreDialogOpen(true)}
              disabled={actionLoading}
              className="btn btn-primary text-xs py-1 px-3 flex items-center gap-1"
            >
              <span>♻️</span>
              <span>Restore Selected</span>
            </button>
            <button
              onClick={() => setBulkDeleteDialogOpen(true)}
              disabled={actionLoading}
              className="btn btn-danger text-xs py-1 px-3 flex items-center gap-1"
            >
              <span>🗑️</span>
              <span>Delete Forever</span>
            </button>
          </div>
        </div>
      )}

      {/* ── Items Table ──────────────────────────── */}
      {loading ? (
        <TableLoader title="Loading Trash records..." />
      ) : (
        <div className="flex-1 min-h-0 bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col overflow-hidden">
          <div className="flex-1 overflow-auto">
            <table className="w-full text-left text-xs sm:text-sm border-collapse">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10 text-gray-600 text-xs font-semibold uppercase tracking-wider">
                <tr>
                  {hasWritePerm && (
                    <th className="p-3 sm:px-4 w-10 text-center">
                      <input
                        type="checkbox"
                        checked={items.length > 0 && selectedIds.size === items.length}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                      />
                    </th>
                  )}
                  <th className="p-3 sm:px-4">Type</th>
                  <th className="p-3 sm:px-4">Item Details</th>
                  <th className="p-3 sm:px-4 hidden md:table-cell">Deleted By</th>
                  <th className="p-3 sm:px-4 hidden sm:table-cell">Deleted On</th>
                  <th className="p-3 sm:px-4">Time Left</th>
                  <th className="p-3 sm:px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={hasWritePerm ? 7 : 6} className="text-center py-16 text-gray-400">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <span className="text-4xl">🗑️</span>
                        <p className="font-semibold text-gray-700 text-base">Trash is Empty</p>
                        <p className="text-xs text-gray-400 max-w-sm">
                          {search || selectedType !== 'all'
                            ? 'No items matched your current filters.'
                            : 'Any deleted certificates, equipment, companies, or personnel will appear here for 30 days.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (

                items.map((item) => {
                  const typeCfg = TYPE_CONFIG[item.entity_type] || {
                    label: item.entity_type,
                    icon: '📄',
                    bg: 'bg-gray-50',
                    border: 'border-gray-200',
                    text: 'text-gray-700',
                  };

                  const isSelected = selectedIds.has(item.id);

                  // Days remaining badge color
                  const daysLeft = item.days_remaining;
                  let daysBadgeClass = 'bg-emerald-50 text-emerald-700 border-emerald-200';
                  if (daysLeft <= 5) {
                    daysBadgeClass = 'bg-red-50 text-red-700 border-red-200 animate-pulse';
                  } else if (daysLeft <= 15) {
                    daysBadgeClass = 'bg-amber-50 text-amber-700 border-amber-200';
                  }

                  return (
                    <tr
                      key={item.id}
                      className={`hover:bg-gray-50/80 transition-colors ${isSelected ? 'bg-brand-50/40' : ''}`}
                    >
                      {hasWritePerm && (
                        <td className="p-3 sm:px-4 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(item.id)}
                            className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                          />
                        </td>
                      )}
                      <td className="p-3 sm:px-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border ${typeCfg.bg} ${typeCfg.border} ${typeCfg.text}`}
                        >
                          <span>{typeCfg.icon}</span>
                          <span>{typeCfg.label}</span>
                        </span>
                      </td>
                      <td className="p-3 sm:px-4">
                        <p className="font-semibold text-gray-900 truncate max-w-[200px] sm:max-w-xs">{item.title}</p>
                        {item.subtitle && (
                          <p className="text-xs text-gray-500 truncate max-w-[200px] sm:max-w-xs mt-0.5">
                            {item.subtitle}
                          </p>
                        )}
                      </td>
                      <td className="p-3 sm:px-4 hidden md:table-cell text-gray-600 text-xs">
                        {item.deleted_by ? (
                          <span className="truncate max-w-[150px] inline-block" title={item.deleted_by}>
                            {item.deleted_by}
                          </span>
                        ) : (
                          <span className="text-gray-400 italic">—</span>
                        )}
                      </td>
                      <td className="p-3 sm:px-4 hidden sm:table-cell text-gray-600 text-xs whitespace-nowrap">
                        {formatDeletedDate(item.deleted_at)}
                      </td>
                      <td className="p-3 sm:px-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${daysBadgeClass}`}>
                          <span>⏳</span>
                          <span>{daysLeft} day{daysLeft === 1 ? '' : 's'} left</span>
                        </span>
                      </td>
                      <td className="p-3 sm:px-4 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          {hasWritePerm && (
                            <>
                              <button
                                onClick={() => setRestoreItem(item)}
                                disabled={actionLoading}
                                title="Restore item"
                                className="btn btn-secondary text-xs py-1 px-2.5 text-brand-700 hover:bg-brand-50 border-brand-200 flex items-center gap-1"
                              >
                                <span>♻️</span>
                                <span className="hidden sm:inline">Restore</span>
                              </button>
                              <button
                                onClick={() => setDeleteForeverItem(item)}
                                disabled={actionLoading}
                                title="Delete permanently"
                                className="btn btn-secondary text-xs py-1 px-2 text-red-600 hover:bg-red-50 border-red-200 flex items-center gap-1"
                              >
                                <span>🗑️</span>
                                <span className="hidden sm:inline">Delete</span>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          onPageChange={setPage}
        />
      </div>
      )}

      {/* ── Confirmation Dialogs ──────────────────── */}

      <ConfirmDialog
        open={!!restoreItem}
        title="Restore Item"
        message={`Are you sure you want to restore "${restoreItem?.title}" back to active records?`}
        confirmLabel="Restore"
        variant="primary"
        onConfirm={() => restoreItem && handleRestore(restoreItem)}
        onCancel={() => setRestoreItem(null)}
      />

      <ConfirmDialog
        open={!!deleteForeverItem}
        title="Delete Permanently"
        message={`Are you sure you want to permanently delete "${deleteForeverItem?.title}"? This cannot be undone.`}
        confirmLabel="Delete Forever"
        variant="danger"
        onConfirm={() => deleteForeverItem && handleDeleteForever(deleteForeverItem)}
        onCancel={() => setDeleteForeverItem(null)}
      />

      <ConfirmDialog
        open={emptyTrashDialogOpen}
        title="Empty Trash"
        message={
          selectedType !== 'all'
            ? `Are you sure you want to permanently delete all ${summary[selectedType as TrashEntityType] || 0} ${TYPE_CONFIG[selectedType as TrashEntityType]?.label || selectedType} item(s) from trash? This cannot be undone.`
            : `Are you sure you want to permanently delete all ${summary.all} item(s) from trash? This cannot be undone.`
        }
        confirmLabel="Empty Trash"
        variant="danger"
        onConfirm={handleEmptyTrash}
        onCancel={() => setEmptyTrashDialogOpen(false)}
      />

      <ConfirmDialog
        open={bulkRestoreDialogOpen}
        title="Restore Selected Items"
        message={`Are you sure you want to restore ${selectedIds.size} selected item(s)?`}
        confirmLabel="Restore All"
        variant="primary"
        onConfirm={handleBulkRestore}
        onCancel={() => setBulkRestoreDialogOpen(false)}
      />

      <ConfirmDialog
        open={bulkDeleteDialogOpen}
        title="Permanently Delete Selected Items"
        message={`Are you sure you want to permanently delete ${selectedIds.size} selected item(s)? This action is permanent and cannot be undone.`}
        confirmLabel="Delete Forever"
        variant="danger"
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkDeleteDialogOpen(false)}
      />
    </div>
  );
}
